/**
 * Shared browser/server contract for a user's LandGate bring-your-own-key connection.
 *
 * The API deliberately returns metadata only. `apiKey` exists solely on the
 * one-shot save request and must never be included in a response or persisted
 * in browser state.
 */
export const LANDGATE_CONNECTION_CONTRACT_VERSION = 1 as const;

export type LandGateConnectionContractVersion =
  typeof LANDGATE_CONNECTION_CONTRACT_VERSION;

export type LandGateAuthScheme = 'bearer' | 'x-api-key';

export type LandGateConnectionState =
  | 'not-configured'
  | 'saved-unverified'
  | 'verified'
  | 'needs-attention';

export type LandGateConnectionErrorCode =
  | 'LANDGATE_CONNECTION_REQUIRED'
  | 'LANDGATE_CONNECTION_CONFLICT'
  | 'LANDGATE_CONNECTION_INVALID'
  | 'LANDGATE_CONNECTION_UNAVAILABLE'
  | 'LANDGATE_CREDENTIAL_REJECTED'
  | 'LANDGATE_ENDPOINT_REJECTED'
  | 'LANDGATE_RATE_LIMITED'
  | 'LANDGATE_REQUEST_TIMEOUT'
  | 'LANDGATE_INVALID_RESPONSE'
  | 'LANDGATE_SITE_BOUNDARY_REQUIRED'
  | 'LANDGATE_UPSTREAM_UNAVAILABLE'
  | 'AUTH_REQUIRED'
  | 'FORBIDDEN'
  | 'UNKNOWN';

/** Safe-to-render connection metadata. Never add credentials to this type. */
export interface LandGateConnectionMetadata {
  contractVersion: LandGateConnectionContractVersion;
  state: LandGateConnectionState;
  revision: number;
  connectionId: string | null;
  credentialVersion: number | null;
  /** Display-safe hostname only; endpoint paths may identify a tenant. */
  endpointHost: string | null;
  authScheme: LandGateAuthScheme | null;
  licenceSharingAttested: boolean;
  savedAt: string | null;
  lastVerifiedAt: string | null;
  /** Sanitized classification only; never expose an upstream response body. */
  lastFailureCode?: LandGateConnectionErrorCode;
}

export interface PutLandGateConnectionRequest {
  contractVersion: LandGateConnectionContractVersion;
  expectedRevision: number;
  endpointUrl: string;
  authScheme: LandGateAuthScheme;
  /** One-shot secret. The server must never echo this value. */
  apiKey: string;
  licenceSharingAttested: true;
}

export interface DeleteLandGateConnectionRequest {
  contractVersion: LandGateConnectionContractVersion;
  expectedRevision: number;
}

export interface LandGateConnectionResponse {
  connection: LandGateConnectionMetadata;
}

export interface LandGateConnectionErrorDetail {
  code: LandGateConnectionErrorCode;
  message: string;
  retryable: boolean;
  requestId: string;
}

export interface LandGateConnectionErrorResponse {
  error: LandGateConnectionErrorDetail;
}

export function isLandGateConnectionState(
  value: unknown,
): value is LandGateConnectionState {
  return value === 'not-configured'
    || value === 'saved-unverified'
    || value === 'verified'
    || value === 'needs-attention';
}

export function isLandGateAuthScheme(value: unknown): value is LandGateAuthScheme {
  return value === 'bearer' || value === 'x-api-key';
}
